import * as React from 'react';
import { Link } from 'react-router-dom';
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { DataGrid } from '@mui/x-data-grid';
import {
    TextField,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download'
import axios from "axios";
import { logout, getLocalToken } from '../utils';

const api = axios.create({
    baseURL: '/admin/api/v1',
});

api.interceptors.request.use((config) => {
    const token = getLocalToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export default function Users() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialSearch = searchParams.get("search") || "";

    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    const [searchPhrase, setSearchPhrase] = useState(initialSearch);
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);

    const [exportLoading, setExportLoading] = useState(false);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 100,
    });

    const [sortModel, setSortModel] = useState([]);

    const [selectedUser, setSelectedUser] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const abortRef = useRef(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(prev => {
                if (prev === searchPhrase) return prev;
                return searchPhrase;
            });

            setPaginationModel(prev => {
                if (prev.page === 0) return prev;
                return { ...prev, page: 0 };
            });
        }, 300);

        return () => clearTimeout(handler);
    }, [searchPhrase]);

    const fetchData = useCallback(async () => {
        const currentRequestId = ++requestIdRef.current;

        // cancel previous request
        if (abortRef.current) {
            abortRef.current.abort();
        }

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            setLoading(true);

            const { page, pageSize } = paginationModel;
            const offset = page * pageSize;

            let query = `?offset=${offset}&limit=${pageSize}`;

            if (debouncedSearch) {
                query += `&search=${encodeURIComponent(debouncedSearch)}`;
            }

            if (sortModel.length > 0) {
                const { field, sort } = sortModel[0];
                query += `&sort_by=${field}&sort_order=${sort}`;
            }

            const res = await api.get(`/users${query}`, {
                signal: controller.signal
            });

            // ignore outdated responses
            if (requestIdRef.current !== currentRequestId) return;

            const payload = res?.data?.payload || {};

            const items = (payload.items || []).map(user => ({
                ...user,
                name: `${user.first_name || ''} ${user.last_name || ''}`.trim()
            }));

            setRows(items);
            setTotalRows(payload.total || 0);

        } catch (err) {
            if (err.name === 'CanceledError') return;

            console.error(err);

            if (err.response?.status === 401) {
                logout();
            }
        } finally {
            if (requestIdRef.current === currentRequestId) {
                setLoading(false);
            }
        }
    }, [paginationModel, debouncedSearch, sortModel]);

    // trigger fetch
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleBlockClick = useCallback((user, e) => {
        e.stopPropagation();
        setSelectedUser(user);
        setConfirmOpen(true);
    }, []);

    const handleConfirmBlock = useCallback(async () => {
        if (!selectedUser) return;

        try {
            setActionLoading(selectedUser.id);

            if (selectedUser.blocked_by_admin) {
                await api.patch(`/users/${selectedUser.id}/unblock`);
            } else {
                await api.patch(`/users/${selectedUser.id}/block`);
            }

            await fetchData();

        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(null);
            setConfirmOpen(false);
            setSelectedUser(null);
        }
    }, [selectedUser, fetchData]);

    const handleCloseDialog = useCallback(() => {
        if (actionLoading) return;
        setConfirmOpen(false);
        setSelectedUser(null);
    }, [actionLoading]);

    const columns = useMemo(() => [
        { field: 'email', headerName: 'Email', width: 270 },
        { field: 'name', headerName: 'Name', width: 270 },
        { field: 'referral', headerName: 'Referral', width: 120 },
        {
            field: 'referred_by',
            headerName: 'Referred by',
            width: 120,
            valueGetter: (value) => {
                return value ? value : 'N/A';
            }
        },
        {
            field: 'referral_count',
            headerName: 'Invited',
            width: 100,
            renderCell: (params) => {
                const count = params.value || 0;
                if(count > 0){
                    return (
                        <Link to={`/users/${params.row.id}/invites`}
                              style={{  color: '#1976d2', textDecoration: 'none', fontWeight: 500}}
                              state={{
                                  referral: params.row.referral,
                                  userName: params.row.name
                              }}
                        >
                            {count}
                        </Link>
                    );
                } else {
                   return count
                }
            }
        },
        {
            field: 'plan_id',
            headerName: 'Subscription',
            width: 220,
            valueGetter: (value, row) => {
                const periodEnd = new Date(row.current_period_end);

                if (!row.current_period_end || Number.isNaN(periodEnd.getTime()) || periodEnd.getTime() <= Date.now()) {
                    return 'N/A';
                }

                // Use plan_id if present, otherwise fall back to product_id
                return row.plan_id || row.product_id || 'N/A';
            }
        },
        {
            field: 'default_profile_type',
            headerName: 'Type',
            width: 110,
            valueGetter: (value) => {
                if(value === 'dating' || value === 'friends'){
                    return 'social'
                }
                if(value === 'business'){
                    return 'business'
                }
                return 'N/A';
            }
        },
        {
            field: 'created',
            headerName: 'Created at',
            width: 200,
            valueGetter: (value) => {
                if (!value) return 'N/A';
                const date = new Date(value);
                return date.toLocaleString(); //uses browser timezone
            }
        },
        {
            field: 'id',
            headerName: 'ID',
            width: 75,
            valueGetter: (value) => {
                if (!value) return 'N/A';
                return value ? value : 'N/A';
            }
        },
        {
            field: 'blocked_by_admin',
            headerName: 'Blocked',
            flex: 1,
            minWidth: 100,
            valueGetter: (value) => {
                return value ? 'Yes' : 'No';
            }
        },
        {
            field: 'actions',
            align: 'right',
            headerName: '',
            sortable: false,
            width: 120,
            renderCell: (params) => {
                const isBlocked = params.row.blocked_by_admin;

                return (
                    <Button
                        variant="contained"
                        color={isBlocked ? "success" : "error"}
                        size="small"
                        startIcon={
                            actionLoading === params.row.id
                                ? null
                                : isBlocked
                                ? <CheckCircleIcon />
                                : <BlockIcon />
                        }
                        onClick={(e) => handleBlockClick(params.row, e)}
                        disabled={actionLoading === params.row.id}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            boxShadow: 'none',
                            minWidth: 95,
                            '&:hover': { boxShadow: 'none' },
                        }}
                    >
                        {actionLoading === params.row.id
                            ? <CircularProgress size={16} />
                            : isBlocked
                                ? 'Unblock'
                                : 'Block'}
                    </Button>
                );
            }
        },
    ], [handleBlockClick, actionLoading]);

    const isBlocked = !!selectedUser?.blocked_by_admin;

    const handleExport = useCallback(async () => {
        try {
            setExportLoading(true);


            const res = await api.get( `/users/export${searchPhrase ? `?search=${encodeURIComponent(searchPhrase)}` : ''}`, {
                responseType: 'blob',
            });

            const blob = new Blob([res.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'users.csv');
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            console.error(err);
        } finally {
            setExportLoading(false);
        }
    }, []);

    return (
        <>
            <Box
                mb={1}
                display="flex"
                justifyContent="space-between"
                alignItems="flex-end"
            >
                <TextField
                    label="Search"
                    variant="standard"
                    size="small"
                    value={searchPhrase}
                    onChange={(e) => setSearchPhrase(e.target.value)}
                    sx={{ minWidth: 220 }}
                />

            </Box>
            <br/>
            <Box
                mb={1}
                display="flex"
                alignItems="center"
            >
                <Button
                    variant="contained"
                    onClick={handleExport}
                    startIcon={
                        exportLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
                    }
                    size="small"
                    disabled={exportLoading}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        height: 32,
                        fontSize: 12,
                        px: 1.5,
                        float: 'right'
                    }}
                >
                    {exportLoading ? 'Exporting...' : 'Export CSV'}
                </Button>
            </Box>
            <DataGrid
                disableColumnMenu
                rows={rows}
                columns={columns}
                loading={loading}
                rowCount={totalRows}
                paginationMode="server"
                sortingMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortModel={sortModel}
                disableAutosize
                onSortModelChange={(model) => {
                    setSortModel(model);
                    setPaginationModel(prev => ({ ...prev, page: 0 }));
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                sx={{
                    border: 0,
                    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-cell:focus-visible': {
                        outline: 'none',
                    },
                }}
                slots={{
                    noRowsOverlay: () => (
                        <Box p={2}>No users found</Box>
                    )
                }}
            />

            <Dialog
                open={confirmOpen && !!selectedUser}
                onClose={handleCloseDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {isBlocked ? 'Confirm Unblock' : 'Confirm Block'}
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        {isBlocked
                            ? `Do you want to unblock ${selectedUser?.email}?`
                            : `Do you want to block ${selectedUser?.email}?`}
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={handleCloseDialog}
                        disabled={!!actionLoading}
                    >
                        No
                    </Button>

                    <Button
                        onClick={handleConfirmBlock}
                        color={isBlocked ? "success" : "error"}
                        variant="contained"
                        disabled={!!actionLoading}
                    >
                        {actionLoading
                            ? <CircularProgress size={20} />
                            : isBlocked ? 'Unblock' : 'Block'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
